import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Rate, SaveRateRule } from '@spike/boarding-rule-model';
import {
    RateRulesStatus,
    fetchRateRulesThunk,
    saveRateRuleThunk,
    deleteRateRuleThunk
} from '@spike/boarding-rules-action';
import { showSuccessThunk, showErrorThunk } from '@spike/notifications-action';
import { fetchServicesThunk } from 'actions/services/ServicesActions';
import { useApiClientWrapper } from 'hooks';
import { Service } from 'model/Service';
import { RootState } from 'store';

interface BoardingRateRulesContextProps {
    // Data
    rateRules: Rate[];
    boardingServices: Service[];
    marketplaceId: number;

    // Loading states
    isPageLoading: boolean; // Page-level spinner: initial fetch + services not ready
    isSaving: boolean; // Save button loading: active while a save is in flight
    isLoading: boolean; // General rate-rules loading (fetch + save)
    hasError: boolean;

    // Filter state
    selectedFilter: string;
    filterChangeHandler: (filter: string) => void;
    isTrueEmpty: boolean;
    isFilteredEmpty: boolean;

    // Form state
    showForm: boolean;
    selectedRateRule: Rate | null;
    openAddForm: () => void;
    openEditForm: (rateRule: Rate) => void;
    closeForm: () => void;

    // Actions
    saveRateRule: (saveRateRuleData: SaveRateRule) => Promise<void>;
    deleteRateRule: (rateRuleId: number) => Promise<void>;
    toggleActive: (rateRuleId: number) => Promise<void>;
}

const BoardingRateRulesContext = React.createContext<BoardingRateRulesContextProps | null>(null);

export const BoardingRateRulesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    // Redux selectors
    const allRateRules = useSelector((state: RootState) => state.rateRules.rates);
    const rateRulesStatus = useSelector((state: RootState) => state.rateRules.status);
    const servicesLoading = useSelector((state: RootState) => state.services.loading);
    const boardingServices = useSelector(
        (state: RootState) => state.services.servicesByBusinessArea?.boarding?.services ?? []
    );
    const marketplaceId = useSelector((state: RootState) => state.marketplace.marketplace?.id ?? 0);

    // Local state
    const [loading, setLoading] = React.useState({ rateRules: true, initialLoading: true });
    const [showForm, setShowForm] = React.useState(false);
    const [selectedRateRule, setSelectedRateRule] = React.useState<Rate | null>(null);
    const [pageInfo, setPageInfo] = React.useState<{ status: 'active' | 'inactive' }>({ status: 'active' });

    // Fetch guards
    const hasFetchedRateRulesRef = React.useRef(false);
    const hasFetchedServicesRef = React.useRef(false);

    // Fetch rate rules on mount
    React.useEffect(() => {
        if (!hasFetchedRateRulesRef.current) {
            hasFetchedRateRulesRef.current = true;
            dispatch(fetchRateRulesThunk(apiClientWrapper));
        }
    }, [dispatch, apiClientWrapper]);

    // Fetch all boarding services on mount
    React.useEffect(() => {
        if (hasFetchedServicesRef.current) return;
        hasFetchedServicesRef.current = true;

        const ALL_SERVICES_PER_PAGE = 1000;
        dispatch(
            fetchServicesThunk(undefined, undefined, ALL_SERVICES_PER_PAGE, 'active', undefined, undefined, 'boarding')
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update loading state based on status
    React.useEffect(() => {
        if (
            rateRulesStatus === RateRulesStatus.FetchSuccess ||
            rateRulesStatus === RateRulesStatus.Error ||
            rateRulesStatus === RateRulesStatus.SaveSuccess
        ) {
            setLoading(prev => ({ ...prev, rateRules: false, initialLoading: false }));
        }
        if (rateRulesStatus === RateRulesStatus.Fetching || rateRulesStatus === RateRulesStatus.Saving) {
            setLoading(prev => ({ ...prev, rateRules: true }));
        }
    }, [rateRulesStatus]);

    // Filtered data
    const rateRules = React.useMemo(() => {
        if (pageInfo.status === 'active') {
            return allRateRules.filter(rule => rule.active);
        }
        return allRateRules.filter(rule => !rule.active);
    }, [allRateRules, pageInfo.status]);

    // Empty state flags
    const isTrueEmpty = allRateRules.length === 0;
    const isFilteredEmpty = !isTrueEmpty && rateRules.length === 0;

    // Form handlers
    const openAddForm = React.useCallback(() => {
        setSelectedRateRule(null);
        setShowForm(true);
    }, []);

    const openEditForm = React.useCallback((rateRule: Rate) => {
        setSelectedRateRule(rateRule);
        setShowForm(true);
    }, []);

    const closeForm = React.useCallback(() => {
        setShowForm(false);
        setSelectedRateRule(null);
    }, []);

    // Actions
    const saveRateRule = React.useCallback(
        async (saveRateRuleData: SaveRateRule): Promise<void> => {
            await (dispatch as any)(saveRateRuleThunk(apiClientWrapper, saveRateRuleData));
            dispatch(showSuccessThunk(saveRateRuleData.id ? 'Rate rule updated' : 'Rate rule created'));
        },
        [dispatch, apiClientWrapper]
    );

    const deleteRateRule = React.useCallback(
        async (rateRuleId: number): Promise<void> => {
            await (dispatch as any)(deleteRateRuleThunk(apiClientWrapper, rateRuleId));
            dispatch(showSuccessThunk('Rate rule deleted'));
        },
        [dispatch, apiClientWrapper]
    );

    const toggleActive = React.useCallback(
        async (rateRuleId: number): Promise<void> => {
            const rule = allRateRules.find(r => r.id === rateRuleId);
            if (!rule) return;

            const saveRateRuleData: SaveRateRule = {
                id: rule.id,
                name: rule.name,
                marketplaceId: rule.marketplaceId,
                startDate: rule.startDate,
                endDate: rule.endDate,
                minNights: rule.minNights,
                active: !rule.active,
                customerTagList: rule.customerTagList
            };

            try {
                await (dispatch as any)(saveRateRuleThunk(apiClientWrapper, saveRateRuleData));
                dispatch(showSuccessThunk(rule.active ? 'Rate rule deactivated' : 'Rate rule activated'));
            } catch (err: unknown) {
                const responseData = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
                const baseErrors =
                    responseData && Array.isArray(responseData.base) ? (responseData.base as string[]) : null;
                const errorMessage = baseErrors?.[0] ?? 'Failed to update rate rule';
                dispatch(showErrorThunk(errorMessage));
            }
        },
        [dispatch, apiClientWrapper, allRateRules]
    );

    const filterChangeHandler = React.useCallback((filter: string) => {
        setPageInfo({ status: filter as 'active' | 'inactive' });
    }, []);

    const value: BoardingRateRulesContextProps = {
        rateRules,
        boardingServices,
        marketplaceId,
        isPageLoading: loading.initialLoading || servicesLoading,
        isSaving: rateRulesStatus === RateRulesStatus.Saving,
        isLoading: loading.rateRules,
        hasError: rateRulesStatus === RateRulesStatus.Error,
        selectedFilter: pageInfo.status,
        filterChangeHandler,
        isTrueEmpty,
        isFilteredEmpty,
        showForm,
        selectedRateRule,
        openAddForm,
        openEditForm,
        closeForm,
        saveRateRule,
        deleteRateRule,
        toggleActive
    };

    return <BoardingRateRulesContext.Provider value={value}>{children}</BoardingRateRulesContext.Provider>;
};

export const useBoardingRateRules = (): BoardingRateRulesContextProps => {
    const context = React.useContext(BoardingRateRulesContext);
    if (!context) {
        throw new Error('useBoardingRateRules must be used within a BoardingRateRulesProvider');
    }
    return context;
};
